/* =================================================================
   GHOSTFIT V3 — STOREFRONT INTERACTIONS (Shopify-wired)
   =================================================================
   Vanilla JS, no dependencies. Loads only on layout/gf-v3.liquid
   pages (the v4 templateSuffix templates). Wired to Shopify's real
   /cart.js + /cart/add.js endpoints. Skips animations in theme
   editor (Shopify.designMode) to avoid editor lifecycle quirks.

   XSS-safe: all dynamic strings via textContent / createElement;
   only static SVG markup uses insertAdjacentHTML.
   ================================================================= */
(() => {
  'use strict';

  /* DesignMode = theme editor preview. Skip motion + cart polls there. */
  const DESIGN_MODE = !!(window.Shopify && window.Shopify.designMode);

  /* DOM helpers */
  const el = (tag, attrs = {}, ...children) => {
    const node = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) {
      if (v == null) continue;
      if (k === 'class') node.className = v;
      else if (k === 'style') node.setAttribute('style', v);
      else if (k === 'dataset') Object.assign(node.dataset, v);
      else if (k.startsWith('on') && typeof v === 'function') node.addEventListener(k.slice(2), v);
      else node.setAttribute(k, v);
    }
    for (const c of children) {
      if (c == null || c === false) continue;
      node.appendChild(c instanceof Node ? c : document.createTextNode(String(c)));
    }
    return node;
  };
  const svgFromString = (s) => {
    const wrap = document.createElement('div');
    wrap.insertAdjacentHTML('afterbegin', s);
    return wrap.firstElementChild;
  };
  const clear = (n) => { while (n.firstChild) n.removeChild(n.firstChild); };
  const $ = (s, root = document) => root.querySelector(s);
  const $$ = (s, root = document) => root.querySelectorAll(s);

  function trapFocus(container) {
    const FOCUSABLE = 'a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])';
    function onKey(e) {
      if (e.key !== 'Tab') return;
      const items = Array.from(container.querySelectorAll(FOCUSABLE)).filter(n => n.offsetParent !== null);
      if (!items.length) return;
      const first = items[0]; const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', onKey);
    return () => container.removeEventListener('keydown', onKey);
  }

  const liveRegion = el('div', { role: 'status', 'aria-live': 'polite', class: 'sr-only', id: 'gf-live-region' });
  document.body.appendChild(liveRegion);
  function announce(msg) { liveRegion.textContent = ''; setTimeout(() => liveRegion.textContent = msg, 50); }

  const HOODIE_SVG = '<svg viewBox="0 0 200 240"><path d="M40 50 Q40 30 60 30 L80 30 Q90 50 100 50 Q110 50 120 30 L140 30 Q160 30 160 50 L168 90 L150 100 L150 220 Q150 230 140 230 L60 230 Q50 230 50 220 L50 100 L32 90 Z"/></svg>';
  const BAG_SVG = '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M6 6h15l-1.5 9h-12z"/><path d="M6 6 5 3H2"/></svg>';

  /* =================================================================
     1. THEME TOGGLE (light/dark override)
     ================================================================= */
  (() => {
    let stored;
    try { stored = localStorage.getItem('gf_theme'); } catch (_) {}
    if (stored === 'dark' || stored === 'light') {
      document.documentElement.setAttribute('data-theme', stored);
    }
    document.querySelectorAll('[data-theme-toggle]').forEach(btn => {
      btn.addEventListener('click', () => {
        const current = document.documentElement.getAttribute('data-theme')
          || (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
        const next = current === 'dark' ? 'light' : 'dark';
        document.documentElement.setAttribute('data-theme', next);
        try { localStorage.setItem('gf_theme', next); } catch (_) {}
      });
    });
  })();

  /* =================================================================
     2. MOBILE MENU DRAWER
     ================================================================= */
  (() => {
    const menu = $('#mobile-menu');
    if (!menu) return;
    const backdrop = $('#mobile-menu-backdrop');
    const open = $('#nav-hamburger');
    const close = $('#mobile-menu-close');
    let untrap = null;
    function openMenu() {
      menu.classList.add('is-open');
      backdrop?.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      untrap = trapFocus(menu);
      menu.querySelector('a, button')?.focus();
    }
    function closeMenu() {
      menu.classList.remove('is-open');
      backdrop?.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (untrap) untrap();
      open?.focus();
    }
    open?.addEventListener('click', openMenu);
    close?.addEventListener('click', closeMenu);
    backdrop?.addEventListener('click', closeMenu);
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && menu.classList.contains('is-open')) closeMenu(); });
  })();

  /* =================================================================
     3. STATE PILLS (homepage; if present)
     ================================================================= */
  $$('.state-pill').forEach(pill => {
    pill.addEventListener('click', () => {
      $$('.state-pill').forEach(p => p.classList.remove('is-active'));
      pill.classList.add('is-active');
    });
  });

  /* =================================================================
     5+6. PRODUCT DETAIL — variant resolution (color × size)
     Resolves the selected variant from the active swatch + size pill
     against the variant matrix in #pd-variants-json, then syncs the cart
     variant id (#pd-variant-id), price, and — when the variant has one —
     the main image. Works for single-option (size-only, the common case)
     AND two-option (color + size) products. The size/color rows only
     carried display data before, so neither selection updated the cart
     variant; this makes add-to-cart honor the chosen size/color.
     ================================================================= */
  (() => {
    const form = $('#pd-form');
    if (!form) return;
    const swatchRow = $('#swatch-row');
    const sizeRow = $('#size-row');
    if (!swatchRow && !sizeRow) return;

    const variantInput = $('#pd-variant-id');
    const colorName = $('#pd-color-name');
    const mainImg = $('#pd-main-img');
    const priceDisplay = $('#pd-price-display');
    const ctaPrice = $('#pd-cta-price');
    const addBtn = $('#add-to-bag');
    const ctaText = $('#add-to-bag-text');
    const sCtaPrice = $('#sticky-cta-price');
    const modalTotal = $('#pd-pz-total');   // Apple personalize popup's Add-to-Bag price
    /* The CTA label is "Add to Bag — <span#pd-cta-price>$X</span>". Cache its
       first child (the leading text node) so we can flip it to an OOS message
       and back without clobbering the price span (keeps things XSS-safe — no
       innerHTML, only textContent on stable nodes). */
    const ctaLeadNode = ctaText && ctaText.firstChild && ctaText.firstChild.nodeType === 3
      ? ctaText.firstChild : null;
    const ctaLeadText = ctaLeadNode ? ctaLeadNode.textContent : '';

    /* Personalization (v4 PDP): the "(size) (Personalized)" variants are kept
       OUT of the size grid; the shopper opts in by typing into the runtime
       #pd-pers-zones fields (built by gf-personalization-v4.js). When any text
       field there holds a value, resolve() swaps the cart to the matching
       personalized variant (base + add-on). Empty -> stay on the base variant. */
    const persZones = $('#pd-pers-zones');
    function personalizationActive() {
      if (!persZones) return false;
      return Array.prototype.some.call(persZones.querySelectorAll('input'), (i) => {
        const t = (i.getAttribute('type') || 'text').toLowerCase();
        return (t === 'text' || t === 'tel' || t === 'number' || t === 'search') && i.value.trim() !== '';
      });
    }

    let variants = [];
    const dataEl = $('#pd-variants-json');
    if (dataEl) { try { variants = JSON.parse(dataEl.textContent) || []; } catch (e) { variants = []; } }

    function activeValue(row, sel) {
      const el = row && row.querySelector(sel + '.is-active');
      if (!el) return null;
      return el.dataset.name || el.dataset.size || el.dataset.color || null;
    }

    function resolve() {
      if (!variants.length) return;
      const color = activeValue(swatchRow, '.swatch');
      const size = activeValue(sizeRow, '.size-pill');

      /* Re-mark size pills for the active color so an out-of-stock
         color×size combo reads as disabled before the shopper clicks.
         The server pre-marks availability across ALL colors (no-JS
         fallback); here we narrow it to the selected color. */
      if (sizeRow && color != null) {
        sizeRow.querySelectorAll('.size-pill').forEach(pill => {
          const pSize = pill.dataset.size;
          if (pSize == null) return;
          const combo = variants.find(v => {
            const opts = (v.options || []).map(String);
            return opts.indexOf(color) !== -1 && opts.indexOf(pSize) !== -1;
          });
          const ok = !!(combo && combo.available);
          pill.classList.toggle('oos', !ok);
          pill.disabled = !ok;
          pill.setAttribute('aria-disabled', String(!ok));
        });
      }

      const match = variants.find(v => {
        const opts = (v.options || []).map(String);
        return (color == null || opts.indexOf(color) !== -1)
            && (size == null || opts.indexOf(size) !== -1);
      });
      if (!match) return;

      /* When the shopper has entered personalization, swap to the matching
         "(size) (Personalized)" variant so the cart charges the add-on and
         carries the personalized SKU. The personalized pills are hidden from
         the size grid, so this is the only path that selects them. Falls back
         to the base variant when personalization is empty or has no sibling. */
      /* persBlocked: defense-in-depth for products whose metafield advertises
         personalization but which lack "(size) (Personalized)" variants
         (pre-2026-07 regen products). Without it the cart silently charges the
         base price for a personalized item. Set true below whenever
         personalization is active but no available personalized sibling
         resolves. */
      let persBlocked = false;
      let chosen = match;
      if (personalizationActive()) {
        /* Derive the size to personalize: the explicitly selected pill, else the
           size carried by the matched base variant. This way typing a name
           BEFORE tapping a size still swaps to the personalized variant (and its
           price) — otherwise the shopper would add the base SKU at the base price
           with personalization properties attached but no add-on charge. */
        let baseSize = size;
        if (baseSize == null) {
          const matchOpts = (match.options || []).map(String);
          baseSize = matchOpts.find(o => variants.some(v =>
            (v.options || []).map(String).indexOf(o + ' (Personalized)') !== -1)) || null;
        }
        if (baseSize != null) {
          const psize = baseSize + ' (Personalized)';
          const pmatch = variants.find(v => {
            const opts = (v.options || []).map(String);
            return (color == null || opts.indexOf(color) !== -1) && opts.indexOf(psize) !== -1;
          });
          if (pmatch && pmatch.available) chosen = pmatch;
          else persBlocked = true;
        } else {
          /* No "(size) (Personalized)" sibling exists for ANY size — the product
             advertises personalization but has no variant to carry the add-on. */
          persBlocked = true;
        }
      }

      if (variantInput) variantInput.value = chosen.id;
      if (mainImg && match.image) mainImg.src = match.image;

      /* Guard add-to-bag against an out-of-stock combo (was only caught
         at /cart/add.js). Flip the CTA copy to "Out of stock" when the
         combo is unavailable, restoring the normal label when ok. We only
         toggle stable text nodes, so the #pd-cta-price span survives. */
      const ok = !!chosen.available && !persBlocked;
      if (addBtn) addBtn.disabled = !ok;
      if (ctaLeadNode) ctaLeadNode.textContent = persBlocked ? 'Personalization unavailable' : (!chosen.available ? 'Out of stock' : ctaLeadText);
      if (ctaPrice) ctaPrice.style.display = ok ? '' : 'none';

      if (typeof chosen.price === 'number') {
        if (priceDisplay) priceDisplay.textContent = formatMoney(chosen.price);
        if (ctaPrice) ctaPrice.textContent = formatMoney(chosen.price);
        if (sCtaPrice) sCtaPrice.textContent = formatMoney(chosen.price);
        if (modalTotal) modalTotal.textContent = formatMoney(chosen.price);
      }
      /* (A second near-duplicate guard block used to live here; it wrote
         ctaText.textContent directly, which destroyed the #pd-cta-price span
         and detached the cached ctaLeadNode — so the label/price could never
         recover once a combo went OOS or personalization-blocked. The guard
         above already handles disabled state, label, and price via stable
         nodes, so it is the single source of truth.) */
    }

    if (swatchRow) swatchRow.addEventListener('click', e => {
      const sw = e.target.closest('.swatch');
      if (!sw) return;
      swatchRow.querySelectorAll('.swatch').forEach(s => { s.classList.remove('is-active'); s.setAttribute('aria-checked', 'false'); });
      sw.classList.add('is-active');
      sw.setAttribute('aria-checked', 'true');
      if (colorName) colorName.textContent = sw.dataset.name || sw.dataset.color;
      resolve();
    });

    if (sizeRow) sizeRow.addEventListener('click', e => {
      const pill = e.target.closest('.size-pill');
      if (!pill || pill.disabled) return;
      sizeRow.querySelectorAll('.size-pill').forEach(p => { p.classList.remove('is-active'); p.setAttribute('aria-checked', 'false'); });
      pill.classList.add('is-active');
      pill.setAttribute('aria-checked', 'true');
      resolve();
    });

    /* Re-resolve when personalization fields change so the cart variant + price
       track whether the shopper has personalized. #pd-pers-zones is built by
       gf-personalization-v4.js AFTER this runs; a delegated 'input' listener on
       the container still catches those late-added inputs (input events bubble). */
    if (persZones) {
      persZones.addEventListener('input', resolve);
      persZones.addEventListener('change', resolve);
    }
    /* The engine's global Reset (#pd-pers-reset) clears field values directly,
       which fires no 'input' event, so re-resolve on the next microtask (after
       its own click handler has cleared the fields) to revert the price. */
    const persReset = document.querySelector('#pd-pers-reset');
    if (persReset) persReset.addEventListener('click', () => { Promise.resolve().then(resolve); });
  })();

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  /* =================================================================
     8. SHOPIFY CART — real /cart.js + /cart/add.js wiring
     ================================================================= */
  const Cart = (() => {
    const drawer = $('#cart-drawer');
    const backdrop = $('#cart-drawer-backdrop');
    const body = $('#cart-drawer-body');
    const footer = $('#cart-drawer-footer');
    const total = $('#cart-total');
    const badge = $('#cart-badge');
    const meterBar = $('#free-ship-meter');
    let untrap = null;

    function buildEmpty() {
      const wrap = el('div', { class: 'cart-empty' });
      const icon = el('div', { class: 'cart-empty-icon' });
      icon.appendChild(svgFromString(BAG_SVG));
      wrap.appendChild(icon);
      wrap.appendChild(el('p', { style: 'font-size:17px;color:var(--text-primary);font-weight:500;margin-bottom:6px' }, 'Your bag is empty.'));
      wrap.appendChild(el('p', { style: 'font-size:14px' }, 'Pick a school and start shopping.'));
      return wrap;
    }

    function buildLine(item) {
      const row = el('div', { class: 'cart-line' });
      const img = el('div', { class: 'cart-line-image' });
      if (item.image) {
        const i = document.createElement('img');
        i.src = item.image;
        i.alt = item.product_title || '';
        i.loading = 'lazy';
        img.appendChild(i);
      } else {
        img.appendChild(svgFromString(HOODIE_SVG));
      }
      const info = el('div', { class: 'cart-line-info' });
      info.appendChild(el('div', { class: 'cart-line-name' }, item.product_title || item.title));
      /* Personalization line-item properties (start with _) are hidden by Shopify
         by default; in the v4 flow we surface name + number to the customer.
         Keys come from gf-personalization-v4 (zone-scoped + legacy flat). */
      const props = item.properties || {};
      let persName = props._personalization_field_name || props._personalization_name || props['Personalize Name'];
      const persNum = props._personalization_field_number || props._personalization_number || props['Personalize Number'];
      /* Templates may use custom field ids (e.g. _personalization_field_player_name);
         surface the first one so the drawer still shows the personalization. */
      if (!persName && !persNum) {
        for (const k in props) {
          if (k.indexOf('_personalization_field_') === 0 && props[k]) { persName = props[k]; break; }
        }
      }
      if (persName || persNum) {
        const pers = (persName || '') + (persName && persNum ? ' · #' : (persNum ? '#' : '')) + (persNum || '');
        info.appendChild(el('div', { class: 'cart-line-pers' }, 'Personalized: ' + pers));
      }
      if (item.variant_title && item.variant_title !== 'Default Title') {
        info.appendChild(el('div', { class: 'cart-line-meta' }, item.variant_title));
      }
      /* qty stepper + line price */
      const qr = el('div', { class: 'cart-line-qr' });
      const qty = el('div', { class: 'cart-qty' });
      qty.appendChild(el('button', { type: 'button', 'aria-label': 'Decrease quantity', onclick: () => changeQty(item.key, item.quantity - 1) }, '−'));
      qty.appendChild(el('span', {}, String(item.quantity)));
      qty.appendChild(el('button', { type: 'button', 'aria-label': 'Increase quantity', onclick: () => changeQty(item.key, item.quantity + 1) }, '+'));
      qr.appendChild(qty);
      qr.appendChild(el('div', { class: 'cart-line-price' }, formatMoney(item.final_line_price)));
      info.appendChild(qr);
      info.appendChild(el('button', { class: 'cart-line-remove', type: 'button', onclick: () => changeQty(item.key, 0) }, 'Remove'));
      row.appendChild(img); row.appendChild(info);
      return row;
    }

    function buildFreeShipMeter(subtotal) {
      const THRESHOLD = 12500; // $125.00 in cents
      if (!meterBar) return;
      clear(meterBar);
      const remaining = Math.max(0, THRESHOLD - subtotal);
      const pct = Math.min(100, (subtotal / THRESHOLD) * 100);
      meterBar.classList.toggle('free-ship-meter--complete', remaining === 0);
      const text = el('div', { class: 'free-ship-meter-text' });
      if (remaining === 0) {
        text.appendChild(document.createTextNode('🎉 You\'ve unlocked '));
        text.appendChild(el('b', {}, 'free shipping'));
        text.appendChild(document.createTextNode('.'));
      } else {
        text.appendChild(document.createTextNode('Add '));
        text.appendChild(el('b', {}, formatMoney(remaining)));
        text.appendChild(document.createTextNode(' for free shipping.'));
      }
      const bar = el('div', { class: 'free-ship-meter-bar' });
      const fill = el('div', { class: 'free-ship-meter-fill', style: 'width:' + pct + '%' });
      bar.appendChild(fill);
      meterBar.appendChild(text);
      meterBar.appendChild(bar);
    }

    function render(cart) {
      if (!body) return;
      const items = cart?.items || [];
      const count = items.reduce((n, l) => n + l.quantity, 0);
      const subtotal = cart?.total_price || 0;
      if (badge) { badge.textContent = String(count); badge.classList.toggle('is-visible', count > 0); }
      clear(body);
      if (items.length === 0) {
        body.appendChild(buildEmpty());
        if (footer) footer.style.display = 'none';
        return;
      }
      items.forEach(it => body.appendChild(buildLine(it)));
      if (total) total.textContent = formatMoney(subtotal);
      if (footer) footer.style.display = 'block';
      buildFreeShipMeter(subtotal);
    }

    async function refresh() {
      try {
        const r = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
        if (!r.ok) return;
        const cart = await r.json();
        render(cart);
        return cart;
      } catch (_) { /* offline / Shopify hiccup — keep current UI */ }
    }

    async function add(formData) {
      try {
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(formData),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          announce(err.description || 'Could not add to bag');
          return null;
        }
        await refresh();
        announce('Added to bag');
        open();
        return r.json();
      } catch (_) {
        announce('Network error — try again');
        return null;
      }
    }

    async function changeQty(key, quantity) {
      const q = Math.max(0, quantity);
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity: q }),
        });
        await refresh();
        announce(q === 0 ? 'Item removed' : 'Cart updated');
      } catch (_) {}
    }
    /* Back-compat alias for the public API. */
    const removeItem = (key) => changeQty(key, 0);

    function open() {
      drawer?.classList.add('is-open');
      drawer?.setAttribute('aria-hidden', 'false');
      backdrop?.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      if (drawer) untrap = trapFocus(drawer);
      drawer?.querySelector('button, a')?.focus();
    }
    function close() {
      drawer?.classList.remove('is-open');
      drawer?.setAttribute('aria-hidden', 'true');
      backdrop?.classList.remove('is-open');
      document.body.style.overflow = '';
      if (untrap) untrap();
    }

    $('#nav-cart-btn')?.addEventListener('click', () => { refresh(); open(); });
    $('#cart-drawer-close')?.addEventListener('click', close);
    backdrop?.addEventListener('click', close);
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && drawer?.classList.contains('is-open')) close(); });
    /* bfcache restore can resurrect a half-open drawer (invisible but still
       hit-testable) and a stale badge — force a known-closed state + resync. */
    window.addEventListener('pageshow', e => { if (e.persisted) { close(); refresh(); } });

    /* PDP add-to-bag wiring — intercept the product form's submit so we add via
       fetch and open the cart DRAWER instead of navigating to /cart. The
       personalization system (gf-personalization-v4.js) injects properties[...]
       hidden inputs in its OWN submit listener; we defer our serialize to a
       microtask so those have run regardless of listener registration order. */
    const addBtn = $('#add-to-bag');
    const pdForm = $('#pd-form');

    function formToCartItem(form) {
      const item = { quantity: 1, properties: {} };
      for (const [k, v] of new FormData(form).entries()) {
        if (k === 'id') item.id = parseInt(v, 10);
        else if (k === 'quantity') { const q = parseInt(v, 10); if (q > 0) item.quantity = q; }
        else if (k.indexOf('properties[') === 0 && v !== '') item.properties[k.slice(11, -1)] = v;
      }
      if (!Object.keys(item.properties).length) delete item.properties;
      return item;
    }

    async function submitAddToBag() {
      if (!pdForm) return;
      /* #sticky-cta-btn calls pdForm.requestSubmit(), which bypasses the disabled
         #add-to-bag button, so the submit path must honor the
         personalization-unavailable / OOS block too. */
      if (addBtn && addBtn.disabled) return;
      const item = formToCartItem(pdForm);
      if (!item.id) return;
      const text = $('#add-to-bag-text');
      const success = $('#add-success');
      const result = await add(item);
      if (result) open();
      if (result && addBtn) {
        if (text) text.style.display = 'none';
        if (success) success.style.display = 'inline-block';
        addBtn.style.background = 'var(--success)';
        addBtn.style.borderColor = 'var(--success)';
        setTimeout(() => {
          if (text) text.style.display = '';
          if (success) success.style.display = 'none';
          addBtn.style.background = '';
          addBtn.style.borderColor = '';
        }, 1400);
      }
    }

    if (pdForm) {
      pdForm.addEventListener('submit', (e) => {
        e.preventDefault();
        Promise.resolve().then(submitAddToBag);
      });
    }
    $('#sticky-cta-btn')?.addEventListener('click', (e) => {
      e.preventDefault();
      if (pdForm) { try { pdForm.requestSubmit(); } catch (_) { submitAddToBag(); } } else submitAddToBag();
    });

    /* Initial sync (skip in design mode to avoid editor 404 loops) */
    if (!DESIGN_MODE) refresh();

    return { open, close, refresh, add, removeItem };
  })();
  window.GfCart = Cart;

  /* =================================================================
     9. STICKY MOBILE CTA on PDP
     ================================================================= */
  (() => {
    const cta = $('#sticky-cta');
    if (!cta || DESIGN_MODE) return;
    if (window.innerWidth > 768) return;
    const trigger = $('#add-to-bag');
    if (!trigger) { cta.classList.add('is-visible'); return; }
    new IntersectionObserver(entries => entries.forEach(e => cta.classList.toggle('is-visible', !e.isIntersecting)),
      { rootMargin: '0px 0px -80px 0px' }).observe(trigger);
  })();

  /* =================================================================
     10. SCROLL REVEAL — above-fold instant, below-fold on scroll
     ---------------------------------------------------------------
     Content is VISIBLE BY DEFAULT (CSS), so:
       • no-JS users always see content (never stuck hidden),
       • above-the-fold content paints immediately with NO fade.
     This observer only HIDES below-fold elements (.gf-prep) and then
     removes that class as they scroll into view, animating them up.
     Single authority for both vocabularies (.fade-up + .rv).
     ================================================================= */
  (() => {
    const targets = Array.from($$('.fade-up, .rv'));
    if (!targets.length) return;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    /* Editor / reduced-motion / no IntersectionObserver → leave everything visible. */
    if (DESIGN_MODE || reduce || !('IntersectionObserver' in window)) return;

    /* threshold:0 → fire on the first intersecting pixel, so elements taller
       than the viewport still reveal (a positive threshold can never be met). */
    const obs = new IntersectionObserver(entries => entries.forEach(e => {
      if (e.isIntersecting) { e.target.classList.remove('gf-prep'); obs.unobserve(e.target); }
    }), { rootMargin: '0px 0px -8% 0px', threshold: 0 });

    const vh = window.innerHeight || document.documentElement.clientHeight;
    /* Suppress transitions while we apply the initial hide so below-fold
       elements snap to hidden instead of visibly fading out. */
    document.documentElement.classList.add('gf-reveal-init');
    targets.forEach(node => {
      const r = node.getBoundingClientRect();
      const inView = r.top < vh * 0.95 && r.bottom > 0;
      if (inView) return;            // already visible — instant, no animation
      node.classList.add('gf-prep'); // hide; animate in once it scrolls into view
      obs.observe(node);
    });
    /* Force reflow so the hidden state is committed transition-free, then
       restore transitions synchronously (rAF would stall in a background tab,
       leaving transitions disabled and killing the scroll-in animation). */
    void document.documentElement.offsetWidth;
    document.documentElement.classList.remove('gf-reveal-init');
  })();

  /* =================================================================
     11. WISHLIST (sessionStorage; could move to /cart attributes later)
     ================================================================= */
  (() => {
    let saved;
    try { saved = JSON.parse(localStorage.getItem('gf_wishlist_v1') || '[]'); } catch { saved = []; }
    function persist() { try { localStorage.setItem('gf_wishlist_v1', JSON.stringify(saved)); } catch {} }
    function syncUI() {
      $$('[data-wishlist]').forEach(btn => {
        const on = saved.includes(btn.dataset.wishlist);
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }
    document.addEventListener('click', e => {
      const btn = e.target.closest('[data-wishlist]');
      if (!btn) return;
      e.preventDefault(); e.stopPropagation();
      const id = btn.dataset.wishlist;
      const idx = saved.indexOf(id);
      if (idx >= 0) { saved.splice(idx, 1); announce('Removed from wishlist'); }
      else { saved.push(id); announce('Saved to wishlist'); }
      persist(); syncUI();
    });
    syncUI();
  })();

  /* =================================================================
     14. IMAGE LIGHTBOX (PDP gallery — [data-lightbox] frames)
     ================================================================= */
  (() => {
    const lb = $('#gf-lightbox');
    if (!lb) return;
    const img = $('#gf-lightbox-img');
    const closeBtn = $('#gf-lightbox-close');
    let lastTrigger = null;
    function openLightbox(src, alt, trigger) {
      if (!src) return;
      img.src = src;
      img.alt = alt || '';
      lastTrigger = trigger || null;
      lb.classList.add('open');
      lb.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      closeBtn?.focus();
    }
    function closeLightbox() {
      lb.classList.remove('open');
      lb.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      img.src = '';
      lastTrigger?.focus?.();
    }
    document.addEventListener('click', e => {
      const trigger = e.target.closest('[data-lightbox]');
      if (!trigger) return;
      const im = trigger.querySelector('img');
      if (!im) return;
      e.preventDefault();
      openLightbox(im.currentSrc || im.src, im.alt, trigger);
    });
    closeBtn?.addEventListener('click', closeLightbox);
    lb.addEventListener('click', e => { if (e.target === lb) closeLightbox(); });
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && lb.classList.contains('open')) closeLightbox(); });
  })();

  /* =================================================================
     15. SEARCH OVERLAY (nav) — Shopify predictive product search
     ================================================================= */
  (() => {
    const ov = $('#gf-search-overlay');
    if (!ov) return;
    const input = $('#gf-search-input');
    const resBox = $('#gf-search-results');
    let untrap = null;
    let timer = null;
    let lastQuery = '';

    function openOverlay() {
      ov.classList.add('open');
      ov.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      untrap = trapFocus(ov);
      setTimeout(() => input?.focus(), 60);
    }
    function closeOverlay() {
      ov.classList.remove('open');
      ov.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (untrap) untrap();
    }

    function renderEmpty(msg) {
      clear(resBox);
      if (msg) resBox.appendChild(el('div', { class: 'h' }, msg));
    }

    function renderResults(products, query) {
      clear(resBox);
      if (!products.length) {
        resBox.appendChild(el('div', { class: 'h' }, 'No matches — press Enter to search everything'));
        return;
      }
      resBox.appendChild(el('div', { class: 'h' }, 'Products'));
      products.forEach(p => {
        const row = el('a', { class: 'sres', href: p.url });
        const av = el('div', { class: 'av' });
        if (p.image) {
          const im = document.createElement('img');
          im.src = p.image; im.alt = '';
          im.style.width = '100%'; im.style.height = '100%';
          im.style.objectFit = 'cover'; im.style.borderRadius = '6px';
          av.appendChild(im);
        } else {
          av.appendChild(el('span', {}, (p.title || '?').charAt(0).toUpperCase()));
        }
        const mid = el('div', {});
        mid.appendChild(el('div', { class: 'n' }, p.title));
        if (p.price) mid.appendChild(el('div', { class: 'm' }, p.price));
        row.appendChild(av);
        row.appendChild(mid);
        row.appendChild(el('span', { class: 'go' }, 'View →'));
        resBox.appendChild(row);
      });
      const seeAll = el('a', {
        class: 'h',
        href: '/search?q=' + encodeURIComponent(query),
        style: 'display:block;text-align:center;color:var(--accent-deep);cursor:pointer;padding:12px;border-top:1px solid var(--line)',
      }, 'See all results for “' + query + '” →');
      resBox.appendChild(seeAll);
    }

    async function runSearch(q) {
      if (q.length < 2) { renderEmpty('Type to search products and teams'); return; }
      try {
        const url = '/search/suggest.json?q=' + encodeURIComponent(q)
          + '&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last';
        const r = await fetch(url, { headers: { 'Accept': 'application/json' } });
        if (!r.ok) return;
        const data = await r.json();
        if (q !== lastQuery) return; // stale
        const fmtPrice = (v) => { if (!v) return ''; const s = String(v); return /^\d/.test(s) ? '$' + s : s; };
        // Hide internal base/master-inventory products from customer search.
        const isBaseProduct = (t) => /base product/i.test(t || '') || /^\[BASE\]/i.test(t || '') || /^BASE\s*-/i.test(t || '');
        const products = (((data.resources || {}).results || {}).products || [])
          .filter(p => !isBaseProduct(p.title) && p.vendor !== 'Ghostfit Sports Prizes')
          .slice(0, 6)
          .map(p => ({
            title: p.title,
            url: p.url,
            image: (p.featured_image && p.featured_image.url) || p.image || '',
            price: fmtPrice(p.price),
          }));
        renderResults(products, q);
      } catch (_) { /* keep UI */ }
    }

    input?.addEventListener('input', () => {
      const q = input.value.trim();
      lastQuery = q;
      clearTimeout(timer);
      timer = setTimeout(() => runSearch(q), 220);
    });

    document.addEventListener('click', e => {
      const trigger = e.target.closest('[data-gf-search-open]');
      if (trigger) { e.preventDefault(); openOverlay(); renderEmpty('Type to search products and teams'); return; }
    });
    ov.addEventListener('click', e => { if (e.target === ov) closeOverlay(); });
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && ov.classList.contains('open')) closeOverlay(); });
    /* The form's native GET submit (Enter) goes to /search?q= — no JS needed. */
  })();

  /* Listen for cart updates from other tabs */
  if (!DESIGN_MODE) {
    window.addEventListener('focus', () => Cart.refresh());
  }

})();
